import {
    ReactElement,
    useRef,
    useState,
    Dispatch,
    FormEventHandler,
    forwardRef,
    SetStateAction,
    useCallback,
    useImperativeHandle,
    useMemo
} from 'react';
import { Team, UpdateContact, Contact } from '@/api';
import { useUpdateTeamAction } from '../actions';
import {
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UISwitch,
    UIText
} from '@/ui';
import {
    ContactData,
    EditableContactsList,
    ContactsList
} from '@/common/components';
import { sanitisePhone, uuidv4 } from '@/common/util';
import { useCompaniesTranslation } from '../hooks';

type TeamOutOfHoursSettingsSectionProps = { team: Team };

export function TeamOutOfHoursSettingsSection(
    props: TeamOutOfHoursSettingsSectionProps
): ReactElement {
    const { team } = props;

    const formRef = useRef<TeamOutOfHoursSettingsSectionFormRef>(null);
    const updateTeam = useUpdateTeamAction({ teamId: team.id });

    const [isEditing, setIsEditing] = useState(false);

    function onSubmit(data: {
        outOfHoursEnabled: boolean;
        outOfHoursContacts: UpdateContact[];
    }) {
        const { outOfHoursEnabled, outOfHoursContacts } = data;

        updateTeam
            .updateTeam({
                settings: {
                    outOfHoursEnabled
                },
                outOfHoursContacts
            })
            .then(() => {
                setIsEditing(false);
            })
            .catch(() => {}); // Error handled in the action
    }

    return (
        <TeamOutOfHoursSettingsSectionForm
            canEdit={updateTeam.isAllowed}
            ref={formRef}
            initialEnabled={team.settings.outOfHoursEnabled}
            initialOutOfHoursContacts={
                team.outOfHoursContacts as unknown as UpdateContact[]
            }
            loading={updateTeam.loading}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onSubmit={onSubmit}
        />
    );
}

type TeamOutOfHoursSettingsSectionFormProps = {
    canEdit: boolean;
    initialEnabled: boolean;
    initialOutOfHoursContacts: Contact[];
    loading: boolean;
    isEditing: boolean;
    setIsEditing: Dispatch<SetStateAction<boolean>>;
    onSubmit: (data: {
        outOfHoursEnabled: boolean;
        outOfHoursContacts: UpdateContact[];
    }) => void;
};

type TeamOutOfHoursSettingsSectionFormRef = {
    resetForm: () => void;
};

const TeamOutOfHoursSettingsSectionForm = forwardRef<
    TeamOutOfHoursSettingsSectionFormRef,
    TeamOutOfHoursSettingsSectionFormProps
>((props, ref): ReactElement => {
    const {
        canEdit,
        initialEnabled,
        initialOutOfHoursContacts,
        loading,
        isEditing,
        setIsEditing,
        onSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);
    const [outOfHoursEnabled, setOutOfHoursEnabled] = useState(initialEnabled);

    /**
     * Contacts state
     */
    const [contacts, setContacts] = useState<ContactData[]>(
        initialOutOfHoursContacts.map((contact) => ({
            id: uuidv4(),
            isValid: false,
            ...contact
        }))
    );
    const checkContacts = useCallback(
        (enabled: boolean, contacts: ContactData[]): string => {
            if (!enabled) return '';
            const invalidContacts = contacts.filter(
                (contact) => !contact.isValid
            );
            if (invalidContacts.length > 0) {
                return t('shared.teams.contacts_error_incomplete');
            }
            if (contacts.length === 0) {
                return t(
                    'components.team_out_of_hours_settings_section.error_requires_contact'
                );
            }
            return '';
        },
        [t]
    );
    const contactsError = useMemo(() => {
        if (!showRealTimeErrors) {
            return '';
        }
        return checkContacts(outOfHoursEnabled, contacts);
    }, [outOfHoursEnabled, contacts, showRealTimeErrors, checkContacts]);

    /**
     * Form submission
     */
    function submitForm() {
        const mappedContacts = contacts.map((c) => ({
            name: c.name,
            role: c.role,
            phone: sanitisePhone(c.phone)
        }));

        onSubmit({
            outOfHoursEnabled,
            outOfHoursContacts: mappedContacts
        });
    }

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();
        setShowRealTimeErrors(true);

        if (!checkContacts(outOfHoursEnabled, contacts)) {
            setShowRealTimeErrors(false);
            submitForm();
        }
    };

    function resetForm() {
        setOutOfHoursEnabled(initialEnabled);
        setContacts(
            initialOutOfHoursContacts.map((contact) => ({
                id: uuidv4(),
                isValid: false,
                ...contact
            }))
        );
        setShowRealTimeErrors(false);
        setIsEditing(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const sectionTitle = t(
        'components.team_out_of_hours_settings_section.title'
    );

    return (
        <>
            <UISection>
                {isEditing ? (
                    <EditableForm
                        sectionTitle={sectionTitle}
                        outOfHoursEnabled={outOfHoursEnabled}
                        setOutOfHoursEnabled={setOutOfHoursEnabled}
                        contacts={contacts}
                        setContacts={setContacts}
                        contactsError={contactsError}
                        loading={loading}
                        showRealTimeErrors={showRealTimeErrors}
                        canEdit={canEdit}
                        resetForm={resetForm}
                        handleSubmit={handleSubmit}
                    />
                ) : (
                    <NonEditableForm
                        sectionTitle={sectionTitle}
                        outOfHoursEnabled={outOfHoursEnabled}
                        contacts={contacts}
                        onEditClick={() => {
                            setIsEditing(true);
                            setShowRealTimeErrors(false);
                        }}
                        canEdit={canEdit}
                    />
                )}
            </UISection>
        </>
    );
});

function NonEditableForm(props: {
    sectionTitle: string;
    outOfHoursEnabled: boolean;
    contacts: ContactData[];
    onEditClick: () => void;
    canEdit: boolean;
}): ReactElement {
    const { sectionTitle, outOfHoursEnabled, contacts, onEditClick, canEdit } =
        props;

    const { t } = useCompaniesTranslation();

    return (
        <>
            <UISectionHeader
                title={sectionTitle}
                rightButtons={
                    canEdit
                        ? [
                              {
                                  text: t('shared.labels.edit'),
                                  variant: 'textDefault',
                                  type: 'button',
                                  onClick: onEditClick
                              }
                          ]
                        : []
                }
            />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <OutOfHoursDescription />

                    {!outOfHoursEnabled && (
                        <UIText medium type="error">
                            {t('shared.labels.disabled')}
                        </UIText>
                    )}
                    {outOfHoursEnabled && <ContactsList contacts={contacts} />}
                </div>
            </UISectionContent>
        </>
    );
}

function EditableForm(props: {
    sectionTitle: string;
    outOfHoursEnabled: boolean;
    setOutOfHoursEnabled: Dispatch<SetStateAction<boolean>>;
    contacts: ContactData[];
    setContacts: Dispatch<SetStateAction<ContactData[]>>;
    contactsError: string;
    loading: boolean;
    showRealTimeErrors: boolean;
    canEdit: boolean;
    resetForm: () => void;
    handleSubmit: FormEventHandler;
}): ReactElement {
    const {
        sectionTitle,
        outOfHoursEnabled,
        setOutOfHoursEnabled,
        contacts,
        setContacts,
        contactsError,
        loading,
        showRealTimeErrors,
        canEdit,
        resetForm,
        handleSubmit
    } = props;

    const { t } = useCompaniesTranslation();

    return (
        <form onSubmit={handleSubmit} noValidate>
            <UISectionHeader title={sectionTitle} />
            <UISectionContent>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 24
                    }}
                >
                    <OutOfHoursDescription />

                    {/* Toggle for Out of Hours Enabled */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 16
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flex: 1,
                                flexDirection: 'column',
                                gap: 4
                            }}
                        >
                            <UIText>
                                {t(
                                    'components.team_out_of_hours_settings_section.enable_label'
                                )}
                            </UIText>
                            <UIText type="default2">
                                {t(
                                    'components.team_out_of_hours_settings_section.enable_description'
                                )}
                            </UIText>
                        </div>
                        <div style={{ display: 'flex', flex: 1 }}>
                            <UISwitch
                                checked={outOfHoursEnabled}
                                onChange={setOutOfHoursEnabled}
                                disabled={!canEdit}
                            />
                        </div>
                    </div>

                    {/* Schedule configuration - only show when enabled */}
                    {outOfHoursEnabled && (
                        <div>
                            <UIText bold>
                                {t(
                                    'components.team_out_of_hours_settings_section.contacts_title'
                                )}
                            </UIText>
                            <div style={{ marginTop: 8 }}>
                                <EditableContactsList
                                    contacts={contacts}
                                    setContacts={setContacts}
                                    showRealTimeErrors={showRealTimeErrors}
                                    canEdit={canEdit}
                                />
                            </div>
                        </div>
                    )}

                    {contactsError && (
                        <UIText type="error">{contactsError}</UIText>
                    )}
                </div>
            </UISectionContent>
            <UISectionFooter
                actions={[
                    {
                        variant: 'primary',
                        type: 'submit',
                        loading: loading,
                        disabled: loading,
                        text: loading
                            ? t('shared.labels.saving')
                            : t('shared.labels.save')
                    },
                    {
                        text: t('shared.labels.cancel'),
                        type: 'button',
                        disabled: loading,
                        onClick: resetForm
                    }
                ]}
            />
        </form>
    );
}

function OutOfHoursDescription(): ReactElement {
    const { t } = useCompaniesTranslation();
    return (
        <UIText type="default2">
            {t('components.team_out_of_hours_settings_section.explainer')}
        </UIText>
    );
}
